import {
  CeilingGrid,
  DeviceCategoryEnum,
  snapPointToNearestTileCenter,
  calculateTileSizeFromBoundingBox,
} from "@multiconsult-group/multiplacer-core";
import { getStandardRoomSpaceValidation } from "./StandardRoomCompare/standardRoomCompareUtils";
import { IMcBimItem, IPartialSpace, IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { IDevice } from "@multiconsult-group/multiplacer-core";
import { ITile } from "@multiconsult-group/multiplacer-core";
import {
  createFireWallCoverageValidator,
  getFireDeviceSquareBoxesAllCombinationsWithValidations,
  IFireDeviceSquareBoxesWithCoversValidation,
} from "./fireDeviceValidations.utils";
import { PartialMcBimSpace } from "../../utils";

/** IDevice extended with an optional parent ID for sub-objects of multi-tile devices. */
type IValidationDevice = IDevice & { _parentId?: string };

const SUBTILE_PITCH_M = 0.6;

/**
 * Expands multi-tile devices into one sub-object per occupied tile, centered at 0.6m spacing
 * along the device direction — matching the drawn bounding box geometry.
 * Single-tile devices are returned as-is.
 * If snapToGrid is true and ceilingGrids are provided, each sub-center is snapped to its
 * nearest tile center.
 */
const expandDevicesWithSubObjects = (
  devices: IDevice[],
  snapToGrid: boolean,
  ceilingGrids?: CeilingGrid[]
): IValidationDevice[] => {
  const result: IValidationDevice[] = [];
  for (const device of devices) {
    const ts = device.tileSize;
    const resolvedTs = ts ?? (device.boundingBox != null
      ? calculateTileSizeFromBoundingBox(device.boundingBox as number[][])
      : undefined);
    const longSpan = resolvedTs ? Math.max(resolvedTs[0], resolvedTs[1]) : 1;
    const shortSpan = resolvedTs ? Math.min(resolvedTs[0], resolvedTs[1]) : 1;
    if (longSpan <= 1 && shortSpan <= 1) {
      result.push(device);
      continue;
    }
    const coord = device.coordinate as [number, number] | null;
    if (coord == null) {
      result.push(device);
      continue;
    }
    const dir = device.direction as [number, number] | undefined;
    const dirLen = dir ? Math.hypot(dir[0], dir[1]) : 0;
    const devX: [number, number] =
      dirLen > 0 ? [dir![0] / dirLen, dir![1] / dirLen] : [1, 0];
    const devY: [number, number] = [-devX[1], devX[0]];

    for (let i = 0; i < longSpan; i++) {
      for (let j = 0; j < shortSpan; j++) {
        const offsetX = (i - (longSpan - 1) / 2) * SUBTILE_PITCH_M;
        const offsetY = (j - (shortSpan - 1) / 2) * SUBTILE_PITCH_M;
        let subCoord: [number, number] = [
          coord[0] + devX[0] * offsetX + devY[0] * offsetY,
          coord[1] + devX[1] * offsetX + devY[1] * offsetY,
        ];
        if (snapToGrid && ceilingGrids && ceilingGrids.length > 0) {
          subCoord = snapPointToNearestTileCenter(subCoord, ceilingGrids) as [number, number];
        }
        result.push({
          ...device,
          coordinate: subCoord,
          _parentId: device._id,
        });
      }
    }
  }
  return result;
};

const areSameParent = (a: IValidationDevice, b: IValidationDevice): boolean =>
  a._parentId != null && a._parentId === b._parentId;

export enum SPACE_VALIDATION_ID_ENUM {
  SUPPLY_AIR_FLOW = "SUPPLY_AIR_FLOW",
  RETURN_AIR_FLOW = "RETURN_AIR_FLOW",
  DEVICE_COLLISION = "DEVICE_COLLISION",
  DEVICE_COLUMN_COLLISION = "DEVICE_COLUMN_COLLISION",
  STANDARD_ROOM = "STANDARD_ROOM",
  SENSOR_ONE_METER_FROM_AIR_DEVICE = "SENSOR_ONE_METER_FROM_AIR_DEVICE",
  SENSOR_HALF_METER_FROM_WALL = "SENSOR_HALF_METER_FROM_WALL",
  FIRE_DEVICES_MIN_200MM_FROM_WALL = "FIRE_DEVICES_MIN_200MM_FROM_WALL",
  ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE = "ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE",
  FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES = "FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES",
  DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM = "DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM",
  EVERY_DEVICE_IS_INSIDE_SPACE = "EVERY_DEVICE_IS_INSIDE_SPACE",
  LIGHT_DEVICES_MIN_500MM_FROM_WALL = "LIGHT_DEVICES_MIN_500MM_FROM_WALL",
}

export interface ISpaceValidation {
  id: SPACE_VALIDATION_ID_ENUM;
  status: "VALID" | "INVALID" | "WARNING" | "UNKNOWN" | undefined;
  title: string;
  errorText?: string;
  description: string;
  typeOfValidation: "SPATIAL" | "OTHER";
}

export const SPACE_VALIDATIONS: ISpaceValidation[] = [
  {
    id: SPACE_VALIDATION_ID_ENUM.SUPPLY_AIR_FLOW,
    title: "Tilluftsmengde",
    description: "Mengde sammenlignes med verdi som finnes på Revit Space",
    status: undefined,
    typeOfValidation: "OTHER",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.RETURN_AIR_FLOW,
    title: "Avtrekksmengde",
    description: "Mengde sammenlignes med verdi som finnes på Revit Space",
    status: undefined,
    typeOfValidation: "OTHER",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.DEVICE_COLLISION,
    title: "Objekt har ikke samme senterkoordinat",
    description: "Objekt har ikke samme senterkoordinat",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.DEVICE_COLUMN_COLLISION,
    title: "Komponent kolliderer med søyle",
    description: "Komponent kolliderer med IFC-søyle",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.STANDARD_ROOM,
    title: "Overensstemmende med standard rom",
    description: "Overensstemmende med standard rom",
    status: undefined,
    typeOfValidation: "OTHER",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE,
    title: "Røykdetektorer (RZ*) 1m fra tilluft, avtrekk og kjølebaffel",
    description: "Sensor er plassert >1m fra tilluft, avtrekk og kjølebaffel",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.SENSOR_HALF_METER_FROM_WALL,
    title: "Sensor 0.5m fra vegg",
    description: "Sensor 0.5m fra vegg",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_200MM_FROM_WALL,
    title: "Sprinkler 200mm fra vegg",
    description: "Sprinkler 200mm fra vegg",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE,
    title: "Sprinkler dekker alle vegger",
    description:
      "Hvert sprinklerhode dekker et område på 3x4m. Denne regelen kontrollerer at alle vegger er dekket av denne overflaten. Alle mulige rotasjoner testet.",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES,
    title: "Sprinkler min 2m mellom sprinkler",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM,
    title: "Avstand mellom tilluft og avtrekk er minst 1.2m",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.EVERY_DEVICE_IS_INSIDE_SPACE,
    title: "Alle produktene er inne i rommet",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.LIGHT_DEVICES_MIN_500MM_FROM_WALL,
    title: "Lysarmatur minst 500mm fra vegg",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
];

interface ISpaceValArgs {
  singleRoom: IRoomSummary | null;
  singleSpace: PartialMcBimSpace;
  devicesInRoom: IDevice[];
  mcbimItemsInStandardSpace: IMcBimItem[];
  mcbimItemsInSpace?: IMcBimItem[];
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  spatialDevicesOverride?: IValidationDevice[];
  compCodePath: string;
  /** Snap sub-tile centers of multi-tile devices to the nearest ceiling tile center. */
  snapSubCentersToGrid?: boolean;
  /** Ceiling grids for the space — required when snapSubCentersToGrid is true. */
  ceilingGrids?: CeilingGrid[];
}

export const getValidationsForSpace = (args: ISpaceValArgs): ISpaceValidation[] => {
  if (args.singleRoom == null) {
    return [];
  }

  // Expand multi-tile devices into per-tile sub-objects for spatial validations.
  // Non-spatial validations (air flow, standard room) use the original device list
  // to avoid double-counting.
  const spatialDevices =
    args.spatialDevicesOverride ??
    expandDevicesWithSubObjects(
      args.devicesInRoom,
      args.snapSubCentersToGrid ?? false,
      args.ceilingGrids
    );

  return SPACE_VALIDATIONS.filter((validation) => {
    return args.activeSpaceValidationSpecs[validation.id];
  }).map((validation) => {
    switch (validation.id) {
      case SPACE_VALIDATION_ID_ENUM.SUPPLY_AIR_FLOW:
        if (args.singleRoom != null) {
          return getAirFlowValidation(args.singleRoom, args.devicesInRoom, "SUPPLY");
        }
        break;
      case SPACE_VALIDATION_ID_ENUM.RETURN_AIR_FLOW:
        if (args.singleRoom != null) {
          return getAirFlowValidation(args.singleRoom, args.devicesInRoom, "RETURN");
        }
        break;
      case SPACE_VALIDATION_ID_ENUM.DEVICE_COLLISION:
        return getSpaceValidationForDeviceInSameCoordinate(spatialDevices);
      case SPACE_VALIDATION_ID_ENUM.DEVICE_COLUMN_COLLISION:
        return getColumnCollisionValidation(spatialDevices, args.mcbimItemsInSpace ?? []);
      case SPACE_VALIDATION_ID_ENUM.STANDARD_ROOM:
        return getStandardRoomSpaceValidation({
          devices: args.devicesInRoom,
          mcbimItemsInStandardSpace: args.mcbimItemsInStandardSpace,
          compCodePath: args.compCodePath,
        });
      case SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE:
        return getSensorsOneMeterFromSupployAirDeviceSpaceValidation(
          spatialDevices,
          args.singleSpace
        );
      case SPACE_VALIDATION_ID_ENUM.SENSOR_HALF_METER_FROM_WALL:
        return {
          ...validation,
          errorText: "",
          status: sensorsAreHalfAMeterAwayFromWall(spatialDevices, args.singleSpace)
            ? "VALID"
            : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_200MM_FROM_WALL:
        return {
          ...validation,
          errorText: "",
          status: fireDevicesIsAtLeast200mmFromWall(spatialDevices, args.singleSpace)
            ? "VALID"
            : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE:
        return {
          ...validation,
          errorText: "",
          status: getCombinationOfFireDeviceSquares({
            mcbimSpace: args.singleSpace,
            devicesInRoom: spatialDevices,
          }).some((comb) => comb.coverAllWalls)
            ? "VALID"
            : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES:
        return {
          ...validation,
          errorText: "",
          status: fireDevicesMin2mBetweenFireDevices(spatialDevices) ? "VALID" : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM:
        return {
          ...validation,
          errorText: "",
          status:
            getMinAndMaxDistanceBetweenTwoDeviceCategories(
              spatialDevices,
              DeviceCategoryEnum.SUPPLY_AIR_DEVICE,
              DeviceCategoryEnum.RETURN_AIR_DEVICE
            ).min > 1.2
              ? "VALID"
              : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.EVERY_DEVICE_IS_INSIDE_SPACE:
        return getInsideSpaceValidation(spatialDevices, args.singleSpace);
      case SPACE_VALIDATION_ID_ENUM.LIGHT_DEVICES_MIN_500MM_FROM_WALL:
        return {
          ...validation,
          errorText: "",
          status: lightDevicesMin500mmFromWall(spatialDevices, args.singleSpace)
            ? "VALID"
            : "WARNING",
        };
      default:
        break;
    }
    return validation;
  });
};

const lightDevicesMin500mmFromWall = (
  devicesInRoom: IValidationDevice[],
  space: PartialMcBimSpace
): boolean => {
  const lightDevices = devicesInRoom.filter(
    (device) => device.deviceCategory === DeviceCategoryEnum.LIGHT_DEVICE
  );
  return lightDevices.every((device) => {
    const distance = getCoordinateDistanceFromPolygon(
      device.coordinate as number[],
      (space.polygon as number[][]) ?? []
    );
    return distance > 0.5;
  });
};

const getInsideSpaceValidation = (
  devicesInRoom: IValidationDevice[],
  space: PartialMcBimSpace
): ISpaceValidation => {
  const validation = SPACE_VALIDATIONS.find(
    (validation) => validation.id === SPACE_VALIDATION_ID_ENUM.EVERY_DEVICE_IS_INSIDE_SPACE
  ) as ISpaceValidation;
  const nonGridDevices = devicesInRoom.filter((device) => device.grid == null);
  const devicesOutsideOfSpace = getDevicesOutsideOfSpace(nonGridDevices, space);
  let errorText = "";
  if (devicesOutsideOfSpace.length > 0) {
    errorText = `Følgende komponenter er utenfor rommet: ${devicesOutsideOfSpace
      .map((device) => device.compCode)
      .join(", ")}`;
  }
  return {
    ...validation,
    errorText,
    status: devicesOutsideOfSpace.length === 0 ? "VALID" : "WARNING",
  };
};

const getDevicesOutsideOfSpace = (
  devicesInRoom: IDevice[],
  space: PartialMcBimSpace
): IDevice[] => {
  return devicesInRoom.filter((device) => {
    if (device.coordinate == null) {
      return false;
    }
    return !coordinateIsInsidePolygon(
      device.coordinate as [number, number],
      space.polygon as [number, number][]
    );
  });
};

const coordinateIsInsidePolygon = (
  coordinate: [number, number],
  polygon: [number, number][]
): boolean => {
  try {
    const x = coordinate[0];
    const y = coordinate[1];
    let isInside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) {
        isInside = !isInside;
      }
    }
    return isInside;
  } catch (error) {
    console.error("Error in coordinateIsInsidePolygon:", error);
    return false;
  }
};

const getMinAndMaxDistanceBetweenTwoDeviceCategories = (
  devicesInRoom: IValidationDevice[],
  deviceCategory1: DeviceCategoryEnum,
  deviceCategory2: DeviceCategoryEnum
): { min: number; max: number } => {
  const devices1 = devicesInRoom.filter((device) => device.deviceCategory === deviceCategory1);
  const devices2 = devicesInRoom.filter((device) => device.deviceCategory === deviceCategory2);
  let min = Infinity;
  let max = 0;
  devices1.forEach((device) => {
    devices2.forEach((_device) => {
      if (areSameParent(device, _device)) return;
      const distance = getDistanceBetweenToCoordinates(
        device.coordinate as number[],
        _device.coordinate as number[]
      );
      if (distance < min) {
        min = distance;
      }
      if (distance > max) {
        max = distance;
      }
    });
  });
  return { min, max };
};

const fireDevicesMin2mBetweenFireDevices = (devicesInRoom: IValidationDevice[]): boolean => {
  return (
    getMinAndMaxDistanceBetweenSameDeviceCategory(
      devicesInRoom,
      DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
    ).min > 2
  );
};

const getMinAndMaxDistanceBetweenSameDeviceCategory = (
  devicesInRoom: IValidationDevice[],
  deviceCategory: DeviceCategoryEnum
): { min: number; max: number } => {
  const devices = devicesInRoom.filter((device) => device.deviceCategory === deviceCategory);
  let min = Infinity;
  let max = 0;
  devices.forEach((device) => {
    devices.forEach((_device) => {
      if (device._id !== _device._id && !areSameParent(device, _device)) {
        const distance = getDistanceBetweenToCoordinates(
          device.coordinate as number[],
          _device.coordinate as number[]
        );
        if (distance < min) {
          min = distance;
        }
        if (distance > max) {
          max = distance;
        }
      }
    });
  });
  return { min, max };
};


const fireDevicesIsAtLeast200mmFromWall = (
  devicesInRoom: IValidationDevice[],
  space: PartialMcBimSpace
): boolean => {
  const fireDevices = devicesInRoom.filter(
    (device) => device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
  );
  return fireDevices.every((device) => {
    const distance = getCoordinateDistanceFromPolygon(
      device.coordinate as number[],
      (space.polygon as number[][]) ?? []
    );
    return distance > 0.2;
  });
};

const getSensorsOneMeterFromSupployAirDeviceSpaceValidation = (
  devicesInRoom: IValidationDevice[],
  singleSpace: PartialMcBimSpace
): ISpaceValidation => {
  const validation = SPACE_VALIDATIONS.find(
    (validation) => validation.id === SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE
  ) as ISpaceValidation;
  return {
    ...validation,
    errorText: "",
    status: sensorsAreOneMeterAwayFromAirDevices(devicesInRoom, singleSpace) ? "VALID" : "WARNING",
  };
};

const getAirFlowValidation = (
  room: IRoomSummary,
  devicesInRoom: IDevice[],
  supplyOrReturn: "SUPPLY" | "RETURN"
): ISpaceValidation => {
  const totalAirFlowInDevices = devicesInRoom.reduce((acc, device) => {
    if (supplyOrReturn === "SUPPLY") {
      if (
        device.deviceCategory === DeviceCategoryEnum.SUPPLY_AIR_DEVICE &&
        device.supplyAirFlow != null
      ) {
        acc += device.supplyAirFlow;
      }
    }
    if (supplyOrReturn === "RETURN") {
      if (
        device.deviceCategory === DeviceCategoryEnum.RETURN_AIR_DEVICE &&
        device.returnAirFlow != null
      ) {
        acc += device.returnAirFlow;
      }
    }
    return acc;
  }, 0);
  const totalSupplyAirFlowInRoom =
    supplyOrReturn === "SUPPLY" ? (room?.supplyAirFlow ?? 0) : (room?.returnAirFlow ?? 0);
  const roundedRoomAirFlow = Math.round(totalSupplyAirFlowInRoom);
  const roundedDeviceAirFlow = Math.round(totalAirFlowInDevices);
  const roundedDiff = Math.round(totalAirFlowInDevices - totalSupplyAirFlowInRoom);
  const spaceValidation = SPACE_VALIDATIONS.find(
    (validation) =>
      validation.id ===
      (supplyOrReturn === "SUPPLY"
        ? SPACE_VALIDATION_ID_ENUM.SUPPLY_AIR_FLOW
        : SPACE_VALIDATION_ID_ENUM.RETURN_AIR_FLOW)
  ) as ISpaceValidation;
  return {
    ...spaceValidation,
    status: totalAirFlowInDevices === totalSupplyAirFlowInRoom ? "VALID" : "WARNING",
    errorText: `Revit MEP Space: ${roundedRoomAirFlow}, Summerat rom: ${roundedDeviceAirFlow}, Diff: ${roundedDiff}`,
  } as ISpaceValidation;
};

export const getSpaceValidationForDeviceInSameCoordinate = (
  devicesInRoom: IValidationDevice[]
): ISpaceValidation => {
  const numberOfItemsInSameTile = devicesInRoomHasSameCoordinate(devicesInRoom);
  const spaceValidation = SPACE_VALIDATIONS.find(
    (validation) => validation.id === SPACE_VALIDATION_ID_ENUM.DEVICE_COLLISION
  ) as ISpaceValidation;
  return {
    ...spaceValidation,
    status: numberOfItemsInSameTile > 0 ? "WARNING" : "VALID",
    errorText: `Antall objekter i samme platta: ${numberOfItemsInSameTile}`,
  };
};

const getColumnCollisionValidation = (
  devicesInRoom: IValidationDevice[],
  mcbimItemsInSpace: IMcBimItem[]
): ISpaceValidation => {
  const validation = SPACE_VALIDATIONS.find(
    (validation) => validation.id === SPACE_VALIDATION_ID_ENUM.DEVICE_COLUMN_COLLISION
  ) as ISpaceValidation;
  const columnPolygons = mcbimItemsInSpace
    .filter((item) => item.instanceClass === "IfcColumn")
    .map((item) => getMcbimItemCollisionPolygon(item))
    .filter((polygon): polygon is number[][] => polygon != null);

  if (columnPolygons.length === 0) {
    return {
      ...validation,
      errorText: "",
      status: "VALID",
    };
  }

  const collidingDeviceLabels = devicesInRoom.reduce((acc, device) => {
    const devicePolygon = getDeviceCollisionPolygon(device);
    if (devicePolygon == null) {
      return acc;
    }

    const collidesWithColumn = columnPolygons.some((columnPolygon) =>
      polygonsIntersect(devicePolygon, columnPolygon)
    );

    if (collidesWithColumn) {
      acc.add(device.compCode || device.description || String(device._id ?? "Ukjent komponent"));
    }

    return acc;
  }, new Set<string>());

  return {
    ...validation,
    status: collidingDeviceLabels.size > 0 ? "WARNING" : "VALID",
    errorText:
      collidingDeviceLabels.size > 0
        ? `Følgende komponenter kolliderer med søyle: ${Array.from(collidingDeviceLabels).join(", ")}`
        : "",
  };
};

const getMcbimItemCollisionPolygon = (item: IMcBimItem): number[][] | null => {
  const polygon = item.polygon ?? item.bbox;
  return isValidPolygon(polygon) ? polygon : null;
};

const getDeviceCollisionPolygon = (device: IValidationDevice): number[][] | null => {
  const coordinate = device.coordinate;
  const direction = device.direction;
  if (!Array.isArray(coordinate) || coordinate.length < 2) {
    return isValidPolygon(device.boundingBox) ? (device.boundingBox as number[][]) : null;
  }

  const tileSize =
    device.tileSize ??
    (isValidPolygon(device.boundingBox)
      ? calculateTileSizeFromBoundingBox(device.boundingBox as number[][])
      : undefined);

  const spans =
    Array.isArray(tileSize) && tileSize.length === 2 && tileSize.every((value) => value > 0)
      ? tileSize
      : [1, 1];
  const longSpan = Math.max(spans[0], spans[1]);
  const shortSpan = Math.min(spans[0], spans[1]);
  const directionLength =
    Array.isArray(direction) && direction.length >= 2 ? Math.hypot(direction[0], direction[1]) : 0;
  const unitDirection =
    directionLength > 0
      ? ([direction![0] / directionLength, direction![1] / directionLength] as const)
      : ([1, 0] as const);
  const normal = [-unitDirection[1], unitDirection[0]] as const;
  const halfLength = (longSpan * SUBTILE_PITCH_M) / 2;
  const halfWidth = (shortSpan * SUBTILE_PITCH_M) / 2;

  const corners = [
    [-halfLength, -halfWidth],
    [halfLength, -halfWidth],
    [halfLength, halfWidth],
    [-halfLength, halfWidth],
  ].map(([along, across]) => [
    coordinate[0] + unitDirection[0] * along + normal[0] * across,
    coordinate[1] + unitDirection[1] * along + normal[1] * across,
  ]);

  return [...corners, corners[0]];
};

const isValidPolygon = (polygon: unknown): polygon is number[][] =>
  Array.isArray(polygon) &&
  polygon.length >= 4 &&
  polygon.every(
    (point) =>
      Array.isArray(point) &&
      point.length >= 2 &&
      typeof point[0] === "number" &&
      typeof point[1] === "number" &&
      Number.isFinite(point[0]) &&
      Number.isFinite(point[1])
  );

const polygonsIntersect = (polygonA: number[][], polygonB: number[][]): boolean => {
  const a = normalizePolygon(polygonA);
  const b = normalizePolygon(polygonB);
  if (a.length < 3 || b.length < 3) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (segmentsIntersect(a1, a2, b1, b2)) {
        return true;
      }
    }
  }

  return pointInPolygon(a[0], b) || pointInPolygon(b[0], a);
};

const normalizePolygon = (polygon: number[][]): number[][] => {
  if (polygon.length <= 1) {
    return polygon;
  }
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? polygon.slice(0, -1) : polygon;
};

const segmentsIntersect = (
  a1: number[],
  a2: number[],
  b1: number[],
  b2: number[]
): boolean => {
  const eps = 1e-9;
  const direction = (p1: number[], p2: number[], p3: number[]) =>
    (p3[0] - p1[0]) * (p2[1] - p1[1]) - (p2[0] - p1[0]) * (p3[1] - p1[1]);
  const onSegment = (p1: number[], p2: number[], p: number[]) =>
    Math.min(p1[0], p2[0]) - eps <= p[0] &&
    p[0] <= Math.max(p1[0], p2[0]) + eps &&
    Math.min(p1[1], p2[1]) - eps <= p[1] &&
    p[1] <= Math.max(p1[1], p2[1]) + eps;

  const d1 = direction(a1, a2, b1);
  const d2 = direction(a1, a2, b2);
  const d3 = direction(b1, b2, a1);
  const d4 = direction(b1, b2, a2);

  if (
    ((d1 > eps && d2 < -eps) || (d1 < -eps && d2 > eps)) &&
    ((d3 > eps && d4 < -eps) || (d3 < -eps && d4 > eps))
  ) {
    return true;
  }

  return (
    (Math.abs(d1) <= eps && onSegment(a1, a2, b1)) ||
    (Math.abs(d2) <= eps && onSegment(a1, a2, b2)) ||
    (Math.abs(d3) <= eps && onSegment(b1, b2, a1)) ||
    (Math.abs(d4) <= eps && onSegment(b1, b2, a2))
  );
};

const pointInPolygon = (point: number[], polygon: number[][]): boolean => {
  const x = point[0];
  const y = point[1];
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const devicesInRoomHasSameCoordinate = (devicesInRoom: IValidationDevice[]): number => {
  return devicesInRoom.reduce((acc, device) => {
    if (device.coordinate == null) {
      return acc;
    }
    const otherDevices = devicesInRoom.filter(
      (_device) =>
        _device._id !== device._id &&
        _device.coordinate != null &&
        !areSameParent(device, _device)
    );
    // Check if any other device has a coordinate less than 0.1 meters away
    const hasCloseCoordinate = otherDevices.some(
      (_device) =>
        getDistanceBetweenToCoordinates(
          device.coordinate as number[],
          _device.coordinate as number[]
        ) < 0.1
    );
    if (hasCloseCoordinate) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

export const sensorsAreOneMeterAwayFromAirDevices = (
  devicesInRoom: IValidationDevice[],
  space: PartialMcBimSpace
): boolean => {
  if (space == null) {
    return false;
  }
  const airDevices = devicesInRoom.filter(
    (device) =>
      device.deviceCategory === DeviceCategoryEnum.SUPPLY_AIR_DEVICE ||
      device.deviceCategory === DeviceCategoryEnum.RETURN_AIR_DEVICE ||
      device.deviceCategory === DeviceCategoryEnum.CHILLER
  );
  const fireSensors = devicesInRoom.filter(
    (device) =>
      device.deviceCategory === DeviceCategoryEnum.SENSOR_DEVICE && device.compCode.startsWith("RZ")
  );

  const sensorsAreWithin1MeterFromSupplyAirDevice = fireSensors.some((sensor) => {
    return airDevices.some((supplyDevice) => {
      if (areSameParent(sensor, supplyDevice)) return false;
      const distance = getDistanceBetweenToCoordinates(
        sensor.coordinate as number[],
        supplyDevice.coordinate as number[]
      );
      return distance < 1;
    });
  });

  return !sensorsAreWithin1MeterFromSupplyAirDevice;
};

export const sensorsAreHalfAMeterAwayFromWall = (
  devicesInRoom: IValidationDevice[],
  space: PartialMcBimSpace
) => {
  const sensors = devicesInRoom.filter(
    (device) => device.deviceCategory === DeviceCategoryEnum.SENSOR_DEVICE
  );
  return sensors.every((sensor) => {
    const distance = getCoordinateDistanceFromPolygon(
      sensor.coordinate as number[],
      (space.polygon as number[][]) ?? []
    );
    return distance > 0.5;
  });
};

export const getDistanceBetweenToCoordinates = (
  coordinate1: number[],
  coordinate2: number[]
): number => {
  const [x1, y1] = coordinate1;
  const [x2, y2] = coordinate2;
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

const getCoordinateDistanceFromPolygon = (coordinate: number[], polygon: number[][]): number => {
  const [x1, y1] = coordinate;

  const distanceToSegment = (
    x: number,
    y: number,
    xA: number,
    yA: number,
    xB: number,
    yB: number
  ): number => {
    const A = x - xA;
    const B = y - yA;
    const C = xB - xA;
    const D = yB - yA;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    const param = lenSq !== 0 ? dot / lenSq : -1;

    let xx, yy;

    if (param < 0) {
      xx = xA;
      yy = yA;
    } else if (param > 1) {
      xx = xB;
      yy = yB;
    } else {
      xx = xA + param * C;
      yy = yA + param * D;
    }

    const dx = x - xx;
    const dy = y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  let minDistance = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const [xA, yA] = polygon[i];
    const [xB, yB] = polygon[(i + 1) % polygon.length];
    const distance = distanceToSegment(x1, y1, xA, yA, xB, yB);
    if (distance < minDistance) {
      minDistance = distance;
    }
  }

  return minDistance;
};

export const allValidationsAreValid = (spaceValidations: ISpaceValidation[]): boolean => {
  return spaceValidations.every((validation) => validation.status === "VALID");
};

interface IArg {
  devicesInRoom: IDevice[];
  deviceOnTheMoveId: string;
  tile: ITile;
  room: IRoomSummary;
  space: IMcBimItem | IPartialSpace;
  mcbimItemsInStandardSpace: IMcBimItem[];
  mcbimItemsInSpace?: IMcBimItem[];
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  compCodePath: string;
}

type TileValidationContext = {
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  compCodePath: string;
  fireWallCoverageValidator?: (candidateFireDeviceCoordinates: [number, number][]) => boolean;
  mcbimItemsInStandardSpace: IMcBimItem[];
  mcbimItemsInSpace: IMcBimItem[];
  movingDevice: IDevice;
  room: IRoomSummary;
  space: IMcBimItem | IPartialSpace;
  staticSpatialDevices: IValidationDevice[];
};

const buildDisabledValidationSpecs = (): Record<SPACE_VALIDATION_ID_ENUM, boolean> =>
  Object.values(SPACE_VALIDATION_ID_ENUM).reduce(
    (acc, id) => {
      acc[id] = false;
      return acc;
    },
    {} as Record<SPACE_VALIDATION_ID_ENUM, boolean>
  );

const getRelevantTileValidationIdsForDevice = (
  device: IDevice
): Set<SPACE_VALIDATION_ID_ENUM> => {
  const ids = new Set<SPACE_VALIDATION_ID_ENUM>([
    SPACE_VALIDATION_ID_ENUM.DEVICE_COLLISION,
    SPACE_VALIDATION_ID_ENUM.DEVICE_COLUMN_COLLISION,
    SPACE_VALIDATION_ID_ENUM.EVERY_DEVICE_IS_INSIDE_SPACE,
  ]);

  switch (device.deviceCategory) {
    case DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE:
      // Wall coverage is handled by a precomputed validator below.
      ids.add(SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_200MM_FROM_WALL);
      ids.add(SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES);
      break;
    case DeviceCategoryEnum.LIGHT_DEVICE:
      ids.add(SPACE_VALIDATION_ID_ENUM.LIGHT_DEVICES_MIN_500MM_FROM_WALL);
      break;
    case DeviceCategoryEnum.RETURN_AIR_DEVICE:
    case DeviceCategoryEnum.SUPPLY_AIR_DEVICE:
    case DeviceCategoryEnum.CHILLER:
      ids.add(SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE);
      if (device.deviceCategory !== DeviceCategoryEnum.CHILLER) {
        ids.add(SPACE_VALIDATION_ID_ENUM.DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM);
      }
      break;
    case DeviceCategoryEnum.SENSOR_DEVICE:
      ids.add(SPACE_VALIDATION_ID_ENUM.SENSOR_HALF_METER_FROM_WALL);
      ids.add(SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE);
      break;
    default:
      break;
  }

  return ids;
};

const getActiveTileValidationSpecsForDevice = (
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>,
  movingDevice: IDevice
): {
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  hasActiveValidation: boolean;
  runFireWallCoverageValidation: boolean;
} => {
  const relevantValidationIds = getRelevantTileValidationIdsForDevice(movingDevice);
  const filteredSpecs = buildDisabledValidationSpecs();
  let hasActiveValidation = false;
  const runFireWallCoverageValidation =
    movingDevice.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE &&
    activeSpaceValidationSpecs[SPACE_VALIDATION_ID_ENUM.ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE] === true;

  for (const validation of SPACE_VALIDATIONS) {
    const isActive =
      validation.typeOfValidation === "SPATIAL" &&
      relevantValidationIds.has(validation.id) &&
      activeSpaceValidationSpecs[validation.id] === true;
    filteredSpecs[validation.id] = isActive;
    hasActiveValidation ||= isActive;
  }
  filteredSpecs[SPACE_VALIDATION_ID_ENUM.ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE] = false;

  return {
    activeSpaceValidationSpecs: filteredSpecs,
    hasActiveValidation: hasActiveValidation || runFireWallCoverageValidation,
    runFireWallCoverageValidation,
  };
};

export const createTileValidationContext = (args: Omit<IArg, "tile">): TileValidationContext | null => {
  const movingDevice = args.devicesInRoom.find((device) => device._id === args.deviceOnTheMoveId);
  if (!movingDevice) {
    return null;
  }

  const { activeSpaceValidationSpecs, hasActiveValidation, runFireWallCoverageValidation } =
    getActiveTileValidationSpecsForDevice(args.activeSpaceValidationSpecs, movingDevice);
  if (!hasActiveValidation) {
    return null;
  }

  const staticSpatialDevices = expandDevicesWithSubObjects(
    args.devicesInRoom.filter((device) => device._id !== args.deviceOnTheMoveId),
    false
  );

  return {
    activeSpaceValidationSpecs,
    compCodePath: args.compCodePath,
    fireWallCoverageValidator: runFireWallCoverageValidation
      ? createFireWallCoverageValidator({
          spacePolygon: args.space?.polygon ?? [],
          fireDeviceCoordinates: staticSpatialDevices.reduce(
            (coordinates, device) => {
              if (
                device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE &&
                device.coordinate != null
              ) {
                coordinates.push([device.coordinate[0], device.coordinate[1]]);
              }
              return coordinates;
            },
            [] as [number, number][]
          ),
          squareSize: {
            width: 2.05,
            height: 1.55,
          },
        })
      : undefined,
    mcbimItemsInStandardSpace: args.mcbimItemsInStandardSpace,
    mcbimItemsInSpace: args.mcbimItemsInSpace ?? [],
    movingDevice,
    room: args.room,
    space: args.space,
    staticSpatialDevices,
  };
};

export const isTileInCeilingGridValidWithContext = (
  context: TileValidationContext,
  tile: ITile
): boolean => {
  const movedDevice = {
    ...context.movingDevice,
    coordinate: tile.center as [number, number],
  };

  const movedSpatialDevices = expandDevicesWithSubObjects([movedDevice], false);
  const validationsInSpaceWithDeviceOnTheMoveInTile = getValidationsForSpace({
    singleRoom: context.room,
    singleSpace: context.space,
    devicesInRoom: [],
    mcbimItemsInStandardSpace: context.mcbimItemsInStandardSpace,
    mcbimItemsInSpace: context.mcbimItemsInSpace,
    activeSpaceValidationSpecs: context.activeSpaceValidationSpecs,
    spatialDevicesOverride: [...context.staticSpatialDevices, ...movedSpatialDevices],
    compCodePath: context.compCodePath,
  });

  if (!validationsInSpaceWithDeviceOnTheMoveInTile.every((validation) => validation.status === "VALID")) {
    return false;
  }

  if (!context.fireWallCoverageValidator) {
    return true;
  }

  const movedFireCoordinates = movedSpatialDevices.reduce(
    (coordinates, device) => {
      if (
        device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE &&
        device.coordinate != null
      ) {
        coordinates.push([device.coordinate[0], device.coordinate[1]]);
      }
      return coordinates;
    },
    [] as [number, number][]
  );

  return context.fireWallCoverageValidator(movedFireCoordinates);
};

export const isTileInCeilingGridValid = (args: IArg): boolean => {
  const context = createTileValidationContext(args);
  if (!context) {
    return true;
  }

  return isTileInCeilingGridValidWithContext(context, args.tile);
};

interface ICombArgs {
  mcbimSpace: PartialMcBimSpace;
  devicesInRoom: IDevice[];
}

export const getCombinationOfFireDeviceSquares = (
  args: ICombArgs
): IFireDeviceSquareBoxesWithCoversValidation[] => {
  const fireDeviceCoordinates = args.devicesInRoom.reduce(
    (acc, device) => {
      if (device.deviceCategory === "FIRE_SUPPRESSION_DEVICE") {
        if (device.coordinate != null) {
          acc.push([device.coordinate[0], device.coordinate[1]]);
        }
      }
      return acc;
    },
    [] as [number, number][]
  );

  return getFireDeviceSquareBoxesAllCombinationsWithValidations({
    spacePolygon: args.mcbimSpace?.polygon ?? [],
    fireDeviceCoordinates,
    squareSize: {
      width: 2.05,
      height: 1.55,
    },
  });
};
